import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Alert,
  Divider,
  Select,
  MenuItem,
  Card,
  CardContent,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import emailTemplateService, {
  EmailTemplate,
  ContextOption,
  EmailTemplateCreate,
  AvailableVariable,
} from '../services/emailTemplateService';
import { formatDate } from '../utils/dateUtils';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { useAuth } from '../context/AuthContext';
import ContextSelect from '../components/common/ContextSelect';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import { emailTemplateColumns } from '../config/emailTemplateColumns';
import { getApiError } from '../utils/apiError';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'align',
  'list', 'bullet',
  'link',
];

const QUILL_EMPTY = '<p><br></p>';

const EmailTemplatesPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [success, setSuccess] = useState<string | null>(null);
  useUserTimezone();

  // ReactQuill ref for programmatic variable insertion
  const quillRef = useRef<ReactQuill>(null);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Available variables
  const [availableVariables, setAvailableVariables] = useState<AvailableVariable[]>([]);

  // Contexts from API
  const [contexts, setContexts] = useState<ContextOption[]>([]);
  const contextLabelMap = useMemo(() => new Map(contexts.map(c => [c.value, c.label])), [contexts]);

  // Table framework
  const getSortValue = useCallback((template: EmailTemplate, key: string): any => {
    switch (key) {
      case 'name': return template.name.toLowerCase();
      case 'internal_id': return (template.internal_id || '').toLowerCase();
      case 'context': return template.context.toLowerCase();
      case 'subject': return template.subject.toLowerCase();
      case 'language': return template.language;
      case 'status': return template.is_active ? 'active' : 'inactive';
      case 'createdAt': return template.created_at;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((template: EmailTemplate): string[] => {
    return [template.name, template.internal_id || '', template.context, template.subject];
  }, []);

  const table = useTablePage<EmailTemplate>({
    entityType: 'emailTemplates',
    columns: emailTemplateColumns,
    tenantId: user?.tenant_id ?? null,
    getSortValue,
    getSearchText,
  });

  // Insert variable at cursor in subject field
  const insertVariableIntoSubject = (variableName: string) => {
    const input = document.getElementById('email-subject') as HTMLInputElement;
    if (input) {
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const before = formData.subject.substring(0, start);
      const after = formData.subject.substring(end);
      setFormData({
        ...formData,
        subject: `${before}{${variableName}}${after}`,
      });
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(
          start + variableName.length + 2,
          start + variableName.length + 2
        );
      }, 0);
    }
  };

  // Insert variable at cursor in Quill editor (or into the link popup if it's open)
  const insertVariableIntoContent = (variableName: string) => {
    const editor = quillRef.current?.getEditor();
    const container = editor?.root?.parentElement; // .ql-container
    const linkTooltip = container?.querySelector('.ql-tooltip.ql-editing') as HTMLElement | null;

    if (linkTooltip) {
      const linkInput = linkTooltip.querySelector('input') as HTMLInputElement | null;
      if (linkInput) {
        const start = linkInput.selectionStart ?? linkInput.value.length;
        const end = linkInput.selectionEnd ?? start;
        const newValue = linkInput.value.substring(0, start) + `{${variableName}}` + linkInput.value.substring(end);
        linkInput.value = newValue;
        linkInput.focus();
        const newPos = start + variableName.length + 2;
        linkInput.setSelectionRange(newPos, newPos);
      }
      return;
    }

    if (editor) {
      const range = editor.getSelection(true);
      const index = range ? range.index : editor.getLength();
      editor.insertText(index, `{${variableName}}`);
      editor.setSelection(index + variableName.length + 2, 0);
    }
  };

  // Form data
  const [formData, setFormData] = useState<{
    context: string;
    name: string;
    internal_id: string;
    subject: string;
    html_content: string;
    language: string;
    is_active: boolean;
  }>({
    context: 'PATIENT',
    name: '',
    internal_id: '',
    subject: '',
    html_content: '',
    language: 'en',
    is_active: true,
  });

  useEffect(() => {
    fetchContexts();
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchContexts = async () => {
    try {
      const fetchedContexts = await emailTemplateService.getContexts();
      setContexts(fetchedContexts);
    } catch (err: any) {
      console.error('Failed to load contexts:', err);
    }
  };

  const fetchTemplates = () => {
    table.loadData(() => emailTemplateService.getTemplates(), 'Failed to load email templates');
  };

  const handleEdit = async () => {
    if (!table.selectedItem) return;
    setSaveError(null);
    setEditingTemplate(table.selectedItem);
    setFormData({
      context: table.selectedItem.context,
      name: table.selectedItem.name,
      internal_id: table.selectedItem.internal_id || '',
      subject: table.selectedItem.subject,
      html_content: table.selectedItem.html_content,
      language: table.selectedItem.language || 'en',
      is_active: table.selectedItem.is_active,
    });
    setTabValue(0);
    await loadAvailableVariables(table.selectedItem.context);
    setEditDialogOpen(true);
    table.handleMenuClose();
  };

  const handleAddNew = async () => {
    setSaveError(null);
    setEditingTemplate(null);
    setFormData({
      context: 'PATIENT',
      name: '',
      internal_id: '',
      subject: '',
      html_content: '',
      language: 'en',
      is_active: true,
    });
    setTabValue(0);
    await loadAvailableVariables('PATIENT');
    setEditDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!table.selectedItem || !window.confirm(`Are you sure you want to delete "${table.selectedItem.name}"?`)) {
      return;
    }
    try {
      await emailTemplateService.deleteTemplate(table.selectedItem.id);
      setSuccess('Template deleted successfully');
      fetchTemplates();
    } catch (err: any) {
      table.setError(getApiError(err, 'Failed to delete template'));
    }
    table.handleMenuClose();
  };

  const handleDuplicate = async () => {
    if (!table.selectedItem) return;
    setEditingTemplate(null);
    setFormData({
      context: table.selectedItem.context,
      name: `Copy of ${table.selectedItem.name}`,
      internal_id: '',
      subject: table.selectedItem.subject,
      html_content: table.selectedItem.html_content,
      language: table.selectedItem.language || 'en',
      is_active: table.selectedItem.is_active,
    });
    setTabValue(0);
    await loadAvailableVariables(table.selectedItem.context);
    setEditDialogOpen(true);
    table.handleMenuClose();
  };

  const isHtmlContentEmpty = (html: string) => {
    return !html || html === QUILL_EMPTY || html.trim() === '';
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.subject.trim() || isHtmlContentEmpty(formData.html_content)) {
      setSaveError('Name, subject, and content are required');
      return;
    }

    setSaveError(null);
    try {
      if (editingTemplate) {
        await emailTemplateService.updateTemplate(editingTemplate.id, formData);
        setSuccess('Template updated successfully');
      } else {
        const createPayload: EmailTemplateCreate = formData;
        await emailTemplateService.createTemplate(createPayload);
        setSuccess('Template created successfully');
      }
      setEditDialogOpen(false);
      setFormData({
        context: 'PATIENT',
        name: '',
        internal_id: '',
        subject: '',
        html_content: '',
        language: 'en',
        is_active: true,
      });
      setEditingTemplate(null);
      fetchTemplates();
    } catch (err: any) {
      setSaveError(getApiError(err, 'Failed to save template'));
    }
  };

  const loadAvailableVariables = async (context: string) => {
    try {
      const vars = await emailTemplateService.getAvailableVariables(context);
      setAvailableVariables(vars);
    } catch (err: any) {
      console.error('Failed to load available variables:', err);
    }
  };

  const handleContextChange = async (newContext: string) => {
    setFormData({ ...formData, context: newContext });
    await loadAvailableVariables(newContext);
  };

  const languageLabel = (lang: string) => lang === 'he' ? 'Hebrew' : 'English';

  // Column value renderer
  const getColumnValue = (template: EmailTemplate, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name': return template.name;
      case 'internal_id': return template.internal_id || '';
      case 'context': return <Chip label={contextLabelMap.get(template.context) || template.context} size="small" />;
      case 'subject': return template.subject;
      case 'language': return <Chip label={languageLabel(template.language)} size="small" />;
      case 'status':
        return (
          <Chip
            label={template.is_active ? 'Active' : 'Inactive'}
            color={template.is_active ? 'success' : 'default'}
            size="small"
          />
        );
      case 'createdAt': return formatDate(template.created_at);
      default: return '';
    }
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];
    return [
      { label: 'Edit', icon: <EditIcon sx={{ mr: 2 }} />, onClick: handleEdit },
      { label: 'Duplicate', icon: <ContentCopyIcon sx={{ mr: 2 }} />, onClick: handleDuplicate },
      { label: 'Delete', icon: <DeleteIcon sx={{ mr: 2 }} />, onClick: handleDelete, color: 'error' as const },
    ];
  };

  // Mobile card renderer
  const renderMobileCard = (template: EmailTemplate) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} sx={{ pr: 5 }}>
        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>{template.name}</Typography>
        <Chip label={template.is_active ? 'Active' : 'Inactive'} color={template.is_active ? 'success' : 'default'} size="small" />
      </Box>
      <Box>
        <Chip label={contextLabelMap.get(template.context) || template.context} size="small" sx={{ mr: 1 }} />
        <Typography variant="caption" color="text.secondary">{template.subject}</Typography>
      </Box>
    </>
  );

  // Check if user has email_and_sms_templates permission
  if (!user?.user_privilege?.email_and_sms_templates) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to access email templates. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading email templates:">
      <DataTableToolbar
        {...table.toolbarProps}
        title="Email Templates"
        searchPlaceholder="Search templates by name, context, or subject..."
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ whiteSpace: 'nowrap' }}
          >
            New Template
          </Button>
        }
      />

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {isMobile ? (
        <MobileCardGrid
          data={table.sortedData}
          renderCard={renderMobileCard}
          onMenuClick={table.handleRowClick}
          getItemKey={(t) => t.id}
          emptyIcon={<EmailIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
          emptyMessage="No email templates found"
        />
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody
            data={table.sortedData}
            {...table.tableBodyProps}
            getColumnValue={getColumnValue}
            getRowKey={(t) => t.id}
            onRowClick={table.handleRowClick}
          />
        </DataTableContainer>
      )}

      <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

      {/* Edit Dialog */}
      <ClinicDialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setSaveError(null); }}
        title={editingTemplate ? 'Edit Email Template' : 'Create Email Template'}
        maxWidth="md"
        actions={
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {saveError && (
              <Alert severity="error" onClose={() => setSaveError(null)}>
                {saveError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button onClick={handleSave} variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
                {editingTemplate ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => { setEditDialogOpen(false); setSaveError(null); }} sx={CLINIC_DIALOG_SX.cancelButton}>
                Cancel
              </Button>
            </Box>
          </Box>
        }
      >
        <Box sx={{ pt: 1 }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="Template Details" />
            <Tab label="Content Editor" />
            <Tab label="Variables Reference" />
          </Tabs>

          {/* Tab 0: Template Details */}
          {tabValue === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                  Name
                </Typography>
                <TextField
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  size="small"
                  placeholder="e.g., Meeting Reminder"
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                />
              </Box>

              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                  Internal ID
                </Typography>
                <TextField
                  value={formData.internal_id}
                  onChange={(e) => setFormData({ ...formData, internal_id: e.target.value })}
                  fullWidth
                  size="small"
                  placeholder="Optional internal identifier"
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                />
              </Box>

              <ContextSelect
                contexts={contexts}
                value={formData.context}
                onChange={handleContextChange}
              />

              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                  Language
                </Typography>
                <Select
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value as string })}
                  fullWidth
                  sx={CLINIC_DIALOG_SX.select}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="he">Hebrew</MenuItem>
                </Select>
              </Box>

              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                  Subject
                </Typography>
                <TextField
                  id="email-subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  fullWidth
                  size="small"
                  placeholder="Use {variable_name} for placeholders"
                  helperText="Use {variable_name} to insert variables"
                  InputProps={{
                    style: { ...CLINIC_DIALOG_SX.textField as React.CSSProperties, direction: formData.language === 'he' ? 'rtl' : 'ltr' },
                  }}
                />
              </Box>

              {/* Quick Insert Variables for Subject */}
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  Quick Insert Variables:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {availableVariables.map((variable) => (
                    <Chip
                      key={variable.name}
                      label={variable.name}
                      onClick={() => insertVariableIntoSubject(variable.name)}
                      size="small"
                      icon={<ContentCopyIcon />}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          {/* Tab 1: Content Editor */}
          {tabValue === 1 && (
            <Box sx={{ pt: 2 }}>
              {/* Quick Insert Variables for content */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  Click a variable to insert it at cursor:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {availableVariables.map((variable) => (
                    <Chip
                      key={variable.name}
                      label={variable.name}
                      onClick={() => insertVariableIntoContent(variable.name)}
                      size="small"
                      icon={<ContentCopyIcon />}
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{
                '& .ql-container': { overflow: 'visible' },
                '& .ql-editor': { height: '280px', overflowY: 'auto', direction: formData.language === 'he' ? 'rtl' : 'ltr', textAlign: formData.language === 'he' ? 'right' : 'left' },
              }}>
                <ReactQuill
                  ref={quillRef}
                  value={formData.html_content}
                  onChange={(value) => setFormData({ ...formData, html_content: value })}
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ marginBottom: '50px' }}
                />
              </Box>
            </Box>
          )}

          {/* Tab 2: Variables Reference */}
          {tabValue === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Typography variant="subtitle1">
                Available Variables for {contextLabelMap.get(formData.context) || formData.context} Context:
              </Typography>
              <Divider />
              {availableVariables.map((variable) => (
                <Card key={variable.name} variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {variable.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ my: 1 }}>
                      {variable.description}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          backgroundColor: 'grey.100',
                          p: 1,
                          borderRadius: 1,
                        }}
                      >
                        Example: {variable.example}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      </ClinicDialog>

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} />
    </TablePageLayout>
  );
};

export default EmailTemplatesPage;
